/**
 * The real skyEmployee brand artwork, from the supplied logo pack.
 *
 * Two shapes, because the shell needs both:
 *   - the MARK (the amber figure alone) for the icon slots in the product
 *     switcher and App Preferences, which pair a ~24px square with a separate
 *     text label — the full lockup would render the word twice and be
 *     illegible at that size.
 *   - the WORDMARK (figure + "Employee Experience") for anywhere the product is
 *     branded on its own.
 *
 * Served as <img> rather than inlined, matching src/components/SkyAgent/
 * BrandMarks.tsx: this is two-colour artwork with a fixed amber, so there is
 * nothing to recolour via currentColor, and inlining the path data buys
 * nothing.
 *
 * Light/dark are separate files because the figure flips between #191919 and
 * white while the amber stays put. Both are rendered and toggled with
 * dark:hidden / hidden dark:block — the same swap ApplicationsPage uses for
 * per-app logos — because the app has no JS-readable theme value at this level.
 */

interface BrandMarkProps {
  className?: string;
}

/** The figure alone, square-ish (51×60). Sized by the caller. */
export function SkyEmployeeMark({ className = "" }: BrandMarkProps) {
  return (
    <>
      <img
        src="/dist/img/skyEmployee-icon-light.svg"
        alt=""
        aria-hidden="true"
        className={`${className} object-contain dark:hidden`}
      />
      <img
        src="/dist/img/skyEmployee-icon-dark.svg"
        alt=""
        aria-hidden="true"
        className={`${className} hidden object-contain dark:block`}
      />
    </>
  );
}

interface WordmarkProps extends BrandMarkProps {
  /**
   * "auto" follows the app theme. "onDark" forces the light-on-dark artwork
   * for surfaces that are dark in BOTH themes — the app header bar is
   * `bg-[#242424]` either way, so the theme swap would put dark lettering on a
   * dark bar in light mode.
   */
  surface?: "auto" | "onDark";
}

/**
 * The full lockup ("Employee Experience"). Sized by HEIGHT — the artwork is 403×60, so a
 * width-based class squashes it.
 */
export function SkyEmployeeWordmark({ className = "h-8 w-auto", surface = "auto" }: WordmarkProps) {
  if (surface === "onDark") {
    return (
      <img
        src="/dist/img/skyEmployee-logo-dark.svg"
        alt="Employee Experience"
        className={`${className} object-contain`}
      />
    );
  }

  return (
    <>
      <img
        src="/dist/img/skyEmployee-logo-light.svg"
        alt="Employee Experience"
        className={`${className} object-contain dark:hidden`}
      />
      <img
        src="/dist/img/skyEmployee-logo-dark.svg"
        alt="Employee Experience"
        className={`${className} hidden object-contain dark:block`}
      />
    </>
  );
}
